import { Link, useParams } from "react-router-dom";

import { useProject, useRememberProject } from "@/api/projects";
import { ApiError } from "@/api/client";
import { SubmitChoices } from "@/components/projects/SubmitChoices";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface RouteParams {
  slug?: string;
  [key: string]: string | undefined;
}

export function SubmitPickerPage() {
  const { slug = "" } = useParams<RouteParams>();
  const project = useProject(slug);
  useRememberProject(slug);

  if (project.isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-6 text-sm text-muted-foreground">
        Loading project…
      </div>
    );
  }

  if (project.isError) {
    const isNotFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <Card elevation="raised">
        <CardHeader>
          <CardTitle>{isNotFound ? "Project not found" : "Could not load project"}</CardTitle>
          <CardDescription>
            {isNotFound
              ? `No project with slug ${slug ? `'${slug}'` : "(empty)"}.`
              : "The FlowStack server returned an error."}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!project.data) return null;

  return (
    <div className="space-y-6">
      <div>
        <p className="label-micro text-muted-foreground">
          <Link to="/projects" className="hover:underline">
            Projects
          </Link>{" "}
          /{" "}
          <Link to={`/projects/${project.data.slug}`} className="hover:underline">
            {project.data.slug}
          </Link>{" "}
          / submit
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Submit a workload</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          A Tile is a single computation; a Flow chains tiles into a pipeline from a template.
        </p>
      </div>

      <SubmitChoices slug={project.data.slug} />

      <Card elevation="raised">
        <CardHeader>
          <CardTitle className="text-base">Prefer the SDK?</CardTitle>
          <CardDescription>
            Every submission the GUI builds can also be sent via the SDK. See{" "}
            <Link to="/settings" className="hover:underline">
              Settings
            </Link>{" "}
            for the CLI snippet.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}
